import { Link, createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { Input } from '@/components/ui/input'
import { useParameters } from '@/features/docs/hooks/useGenerationsDocs'
import { getAdjacentItems } from '@/features/docs/config/navigation'
import { DocsBreadcrumb } from '@/features/docs/components/DocsBreadcrumb'
import { DocsCopyButton } from '@/features/docs/components/DocsCopyButton'
import { generateLinks, generateMetaTags } from '@/lib/seo'
import { getBreadcrumbSchema, renderJsonLd } from '@/lib/structured-data'

const SLUG = 'api-reference/generation-parameters'
const TITLE = 'Parameter Reference'

export const Route = createFileRoute('/docs/api-reference/generation-parameters')({
  head: () => ({
    meta: generateMetaTags({
      title: `${TITLE} - ModelStack Docs`,
      description:
        'Complete reference for every parameter supported by the ModelStack Generations API — prompting, media inputs, output, generation control, and more.',
      canonical: `/docs/${SLUG}`,
    }),
    links: generateLinks({ canonical: `/docs/${SLUG}` }),
    scripts: [
      {
        type: 'application/ld+json',
        children: renderJsonLd(
          getBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Docs', url: '/docs' },
            { name: TITLE, url: `/docs/${SLUG}` },
          ]) as any,
        ),
      },
    ],
  }),
  component: ParametersPage,
})
const CATEGORIES = ['Prompting', 'Media Inputs', 'Output', 'Generation Control', 'Video', 'Advanced']

function ParametersPage() {
  const { data, loading, error } = useParameters()
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedParam, setExpandedParam] = useState<string | null>(null)
  const { prev, next } = getAdjacentItems(SLUG)

  const groupedParams = useMemo(() => {
    if (!data) return []
    return CATEGORIES.map((cat) => ({
      category: cat,
      params: data.params
        .filter((p) => p.category === cat)
        .filter((p) => {
          if (!search) return true
          const q = search.toLowerCase()
          return p.name.includes(q) || p.description.toLowerCase().includes(q)
        }),
    })).filter((g) => !selectedCategory || g.category === selectedCategory)
  }, [data, search, selectedCategory])

  return (
    <div className="flex gap-0">
      <div className="flex-1 min-w-0 px-8 py-10 max-w-3xl">
        <DocsBreadcrumb slug={SLUG} />

        <div className="flex items-start justify-between gap-4 mb-2">
          <h1 className="text-3xl font-bold text-[var(--color-text-primary)] tracking-[-0.03em] md:text-4xl">
            Parameter Reference
          </h1>
          <DocsCopyButton slug={SLUG} />
        </div>
        <p className="text-[var(--color-text-secondary)] text-lg mb-8">
          Complete guide to all {data?.total ?? '...'} available generation parameters
        </p>

        <div className="space-y-4 mb-8">
          <Input
            placeholder="Search parameters..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-md"
          />

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-300 ease-brisk active:scale-[0.97] ${
                selectedCategory === null
                  ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] border-[var(--color-primary)] shadow-[0_0_24px_-8px_var(--color-primary-shadow)]'
                  : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-control-surface-hover)] hover:-translate-y-0.5 text-[var(--color-text-secondary)]'
              }`}
            >
              All Parameters
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`px-4 py-2 rounded-full border text-sm font-medium transition-all duration-300 ease-brisk active:scale-[0.97] ${
                  selectedCategory === cat
                    ? 'bg-[var(--color-primary)] text-[var(--color-primary-foreground)] border-[var(--color-primary)] shadow-[0_0_24px_-8px_var(--color-primary-shadow)]'
                    : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-control-surface-hover)] hover:-translate-y-0.5 text-[var(--color-text-secondary)]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading && (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="border border-[var(--color-border)] rounded-2xl p-6 animate-pulse">
                <div className="h-6 bg-[var(--color-bg-tertiary)] rounded-full w-64 mb-3" />
                <div className="h-4 bg-[var(--color-bg-tertiary)] rounded-full w-full" />
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-12 text-[var(--color-error)]">
            <p>Failed to load parameters: {error}</p>
          </div>
        )}

        {!loading && !error && groupedParams.map(({ category, params: catParams }) =>
          catParams.length > 0 && (
            <section key={category} id={`cat-${category.toLowerCase().replace(/\s+/g, '-')}`} className="mb-8 scroll-mt-20">
              <h2 className="text-xl font-bold text-[var(--color-text-primary)] mb-4">{category}</h2>
              <div className="space-y-3">
                {catParams.map((param) => (
                  <ParameterCard
                    key={param.name}
                    param={param}
                    isExpanded={expandedParam === param.name}
                    onToggle={() => setExpandedParam(expandedParam === param.name ? null : param.name)}
                  />
                ))}
              </div>
            </section>
          )
        )}

        {!loading && !error && groupedParams.every((g) => g.params.length === 0) && (
          <div className="text-center py-12 text-[var(--color-text-secondary)]">
            <p>No parameters found matching your search</p>
          </div>
        )}

        {/* Prev/Next navigation */}
        {(prev || next) && (
          <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-[var(--color-border)]">
            {prev ? (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${prev.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group max-w-[45%]"
              >
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_back
                </span>
                <div className="text-left min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">Previous</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {prev.title}
                  </div>
                </div>
              </Link>
            ) : (
              <div />
            )}
            {next && (
              <Link
                // @ts-expect-error - Dynamic doc routes not in generated types
                to={`/docs/${next.slug}`}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 hover:bg-[var(--color-bg-secondary)]/70 hover:backdrop-blur-md hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_var(--color-shadow)] transition-all duration-300 ease-brisk group ml-auto max-w-[45%]"
              >
                <div className="text-right min-w-0">
                  <div className="text-xs text-[var(--color-text-tertiary)] mb-0.5">Next</div>
                  <div className="text-sm font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-primary)] truncate">
                    {next.title}
                  </div>
                </div>
                <span className="material-symbols-outlined text-lg text-[var(--color-text-tertiary)] group-hover:text-[var(--color-primary)] shrink-0">
                  arrow_forward
                </span>
              </Link>
            )}
          </div>
        )}
      </div>
      {/* Right ToC rail */}
      <div className="hidden xl:block w-56 shrink-0 px-4 py-10">
        <nav className="sticky top-24 space-y-3">
          <p className="px-3 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)]">
            On this page
          </p>
          <ul className="space-y-0.5 text-sm">
            {CATEGORIES.map((cat) => (
              <li key={cat}>
                <a
                  href={`#cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                  className="block rounded-full px-3 py-1.5 text-[var(--color-text-secondary)] transition-all duration-300 ease-brisk hover:bg-[var(--color-control-surface-hover)] hover:text-[var(--color-text-primary)]"
                >
                  {cat}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  )
}

function ParameterCard({
  param,
  isExpanded,
  onToggle,
}: {
  param: NonNullable<ReturnType<typeof useParameters>['data']>['params'][0]
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg-secondary)]/70 backdrop-blur-md overflow-hidden transition-colors duration-300 ease-brisk hover:border-[var(--color-primary)]/40">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-start justify-between text-left transition-colors duration-300 ease-brisk hover:bg-[var(--color-bg-tertiary)]"
      >
        <div className="text-left flex-1">
          <div className="flex items-center gap-3">
            <code className="font-mono font-semibold">{param.name}</code>
            <span className="text-xs font-mono bg-[var(--color-bg-tertiary)] px-2.5 py-1 rounded-full text-[var(--color-text-secondary)]">
              {param.type}
            </span>
            {param.default && (
              <span className="text-xs text-[var(--color-text-secondary)]">
                Default: {param.default}
              </span>
            )}
          </div>
          <p className="text-sm text-[var(--color-text-secondary)] mt-2">{param.description}</p>
        </div>
        <span className={`text-2xl text-[var(--color-text-secondary)] transition-transform duration-300 ease-brisk ${isExpanded ? 'rotate-180' : ''}`}>
          ▼
        </span>
      </button>

      {isExpanded && (
        <div className="bg-[var(--color-bg-secondary)] px-6 py-4 space-y-4 border-t border-[var(--color-border)]">
          {param.enumValues && (
            <div>
              <h3 className="font-semibold mb-2">Allowed Values</h3>
              <div className="flex flex-wrap gap-2">
                {param.enumValues.map((val) => (
                  <code
                    key={val}
                    className="text-xs px-2.5 py-1 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-success-text)]"
                  >
                    {val}
                  </code>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="font-semibold mb-2">Used by {param.usedByModels.length} model{param.usedByModels.length !== 1 ? 's' : ''}</h3>
            <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
              {param.usedByModels.slice(0, 20).map((model) => (
                <code
                  key={model}
                  className="text-xs px-2.5 py-1 rounded-full bg-[var(--color-bg-tertiary)] text-[var(--color-text-secondary)]"
                >
                  {model}
                </code>
              ))}
              {param.usedByModels.length > 20 && (
                <span className="text-xs text-[var(--color-text-secondary)]">
                  +{param.usedByModels.length - 20} more
                </span>
              )}
            </div>
          </div>

          <div>
            <h3 className="font-semibold mb-2">Supported Generation Types</h3>
            <div className="flex flex-wrap gap-2">
              {param.usedByTypes.map((type) => (
                <span
                  key={type}
                  className="text-xs px-2.5 py-1 rounded-full border border-[var(--color-border)] text-[var(--color-text-secondary)] font-mono"
                >
                  {type}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
